// @flow
import * as React from 'react';
import { ButtonBase, CircularProgress, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useClient } from '../../hooks/client-context';
import {
  AgentView, ClientCalendlyAppointmentsQuery,
  Portal,
  useClientCalendlyAppointmentsQuery,
} from '../../../../shared';
import { getUserName } from '@coverright/utils';
import moment from 'moment/moment';
import { ArrowSquareOut } from '@phosphor-icons/react';
import _ from 'lodash';
import { Refresh } from '@mui/icons-material';
import { ShowedUpToggle } from '../../../../features';

export const CalendlyAppointments = () => {
  const client = useClient();

  const { data, loading, refetch} = useClientCalendlyAppointmentsQuery({
    variables: {clientId: client.id},
    skip: !client.id,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache'
  })
  const appointments = data?.clientCalendlyAppointments || [];

  if (appointments.length === 0) {
    return null;
  }

  return (<Stack gap={2} flex={1} minWidth={395}>
    <Stack gap={2} direction={'row'} alignItems={'center'} flex={1} minHeight={32}>
      <Typography variant={'subtitle1'} flex={1}>Calendly Appointments</Typography>
      {!loading && <IconButton
        size={'small'}
        onClick={() => refetch()}>
        <Refresh color={'action'}/>
      </IconButton>}
      {loading && <CircularProgress size={20} />}
    </Stack>

    {_.orderBy(appointments, 'createdAt', 'desc').map(a => (
      <Portal key={a.url}>
        <Stack gap={2} direction={'row'} alignItems={'center'} bgcolor={getBgColor(a)} p={'2px 16px'}>
          <Typography flex={1} variant={'caption'} align={'left'}>
            Created {moment(a.createdAt).format('L H:mm')} by {a.createdBy}
          </Typography>
          <ButtonBase onClick={() => window.open(a.url)}>
            <ArrowSquareOut size={16} color="#4c515d" weight="bold" />
          </ButtonBase>
        </Stack>
        <Stack gap={2} p={'6px 16px'} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <AgentView agent={{
            ...a.agent,
            fullName: getUserName(a.agent),
          }} />
          <Stack alignItems={'flex-end'} gap={.5}>
            <Typography variant={'caption'} sx={{color: 'text.secondary', border: '1px solid #9f9f9f', p: '1px 4px', borderRadius: '4px'}}>
              Start time: {moment(a.startTime).format('L H:mm')}
            </Typography>
            {!isCanceled(a) && <Stack gap={1.5} direction={'row'} alignItems={'center'}>
              <Typography variant={'body2'}>Showed up</Typography>
              <ShowedUpToggle appUrl={a.url} noShow={a.noShow} clientId={client.id} />
            </Stack>
            }
            {isCanceled(a) && <Typography color={'white.main'} variant={'body2'} fontWeight={500} sx={{borderRadius: '4px', bgcolor: 'error.500', border: '1px solid #de3124', px: 1}}>
              Canceled
            </Typography>}
          </Stack>
        </Stack>
      </Portal>
    ))}
  </Stack>);
};

const isCanceled = (a: ClientCalendlyAppointmentsQuery['clientCalendlyAppointments'][0]) => a.status === 'CANCELED'

const getBgColor = (a: ClientCalendlyAppointmentsQuery['clientCalendlyAppointments'][0]) => (
  isCanceled(a) ? '#de312433' : moment(a.startTime).isBefore(moment()) ? '#dfdfdf' : '#d1d9ef'
)

